<template>
  <div class="configuration">
    <ButtonExtraWishes
      v-if="device === 'mobile'"
      class="extra-wishes-button"
      @click="openWishesPopup = !openWishesPopup"
    />

    <popup-transition>
      <PopupExtraWishes
        v-if="openWishesPopup"
        v-model="extraWishes"
        @close="openWishesPopup = false"
      />
    </popup-transition>

    <div class="charging-options">
      <EmatBlockDropdown
        v-if="device !== 'mobile'"
        class="block-dropdown option"
        :name="car ? this.$t('charging-options.selected_car') + car : this.$t('charging-options.no_car_selected')"
        :dropdown-title="this.$t('charging-options.best_power') + car + '?'"
        icon="car"
        :drop="false"
        @previous="$emit('previous')"
      />
      <ChargeTimes class="option" />
    </div>

    <div style="display: flex; margin-top: 11px; align-items: center">
      <EmatInfo v-if="infoPopupType" v-bind="infoPopupType" />
      <EmatTabs
        style="margin-top: 0; width: 100%;"
        :tabs="typeTabs"
        @change="changeType"
        v-model="activeType"
      />
    </div>

    <transition name="fade-move-left" mode="out-in">
      <ChargingStationMenu v-if="type && type.type === 'laadpaal'" :type="type" />
      <ChargingPillarMenu v-if="type && type.type === 'laadzuil'" :type="type" />
    </transition>

    <emat-button
      class="open-mobile-menu"
      :class="{open: mobileConfig}"
      v-if="device === 'mobile'"
      :mobile="device"
      @click="mobileConfig = !mobileConfig"
      bgColor="blue"
    >
      <TriangleSvg :class="{active: mobileConfig}" />
      <span v-if="type && !mobileConfig">{{ $t('global.word.view') }} {{ type.type }}</span>
      <span v-else>{{ $t('global.word.configure') }} {{ type.type }}</span>
    </emat-button>

    <transition name="fade-move-left-mobile" mode="out-in">
      <div class="mobile-configuration" v-if="device === 'mobile' && mobileConfig">
        <ChargingStationImage v-if="type && type.type === 'laadpaal'" />
        <ChargingPillarImage v-else />
      </div>
    </transition>

    <div class="mobile-summary" v-if="device === 'mobile'" :class="{'open': mobileSummary}">
      <div class="overview-toggle" @click="mobileSummary = !mobileSummary">
        <div class="overview-label">
          <img :src="asset('/assets/images/icons/triangle-light-blue.svg')" alt="" class="chevron">
          <span>{{ $t('global.word.summary') }}</span>
        </div>
        <div class="delivery">
          <GreenCheckSvg class="icon" />
          <span class="time">Levertijd <span class="strong">{{ deliveryTime }}</span></span>
        </div>
      </div>
      <div class="mobile-overview" v-if="mobileSummary">
        <emat-order-summary
          :order="true"
          :device="device"
          :name="'EVhub ' +  type.name + ' ' + configuration.Kleur"
          :options="configuration"
          :products="productSummary"
          :type="type"
          :footer="true"
        >
          <template slot="footer">
            <template v-if="!method.with_plugs">
              <WcdSvg style="width: 47px; height: auto;" />
              <span class="text">WCD</span>
            </template>
            <template v-else>
              <img style="width: 47px; height: auto;" v-if="plug" :src="plug.image_url" alt="">
              <span class="text"><strong>{{ plug.name }}</strong>, {{ method.name }} {{ method.extra_name }}</span>
            </template>
          </template>
        </emat-order-summary>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EmatTabs from 'Components/Inputs/Tabs';
import EmatBlockDropdown from 'Components/Layout/BlockDropdown';
import EmatButton from 'Components/General/Button';
import EmatOrderSummary from 'Components/Layout/OrderSummary';
import EmatInfo from 'Components/General/Info';
import ChargingPillarMenu from './ChargingPillarMenu';
import ChargingStationMenu from './ChargingStationMenu';
import ChargingStationImage from './image/ChargingStationImage';
import ChargingPillarImage from './image/ChargingPillarImage';
import ChargeTimes from '../modal/ChargeTimes';
import updateSettingMixin from '@/mixins/updateSettingMixin';
import ButtonExtraWishes from 'Components/Buttons/ExtraWishes/ButtonExtraWishes';
import PopupExtraWishes from 'Components/Layout/Popups/ExtraWishes/PopupExtraWishes';
import PopupTransition from 'Components/Layout/Popups/Transition/PopupTransition';

import GreenCheckSvg from '../../assets/images/icons/checkmark-green.svg?inline';
import WcdSvg from 'Components/../assets/icons/wcd.svg?inline';
import TriangleSvg from 'Components/../assets/icons/triangle-light-blue.svg?inline';
import helpers from '@/mixins/helpers';

export default {
  name: 'Configuration',

  mixins: [updateSettingMixin, helpers],

  components: {
    PopupExtraWishes,
    PopupTransition,
    EmatTabs,
    EmatBlockDropdown,
    EmatButton,
    EmatInfo,
    ChargingPillarMenu,
    ChargingStationMenu,
    EmatOrderSummary,
    ChargeTimes,
    ChargingStationImage,
    ChargingPillarImage,
    ButtonExtraWishes,
    GreenCheckSvg,
    WcdSvg,
    TriangleSvg,
  },

  data() {
    return {
      activeType: 1,
      mobileConfig: false,
      mobileSummary: false,
      openWishesPopup: false,
    };
  },

  computed: {
    ...mapGetters({
      deliveryDays: 'build/deliveryDays',
      type: 'types/current',
      types: 'types/mappedById',
      selectedBrand: 'build/getBrand',
      selectedModel: 'build/getModel',
      device: 'getDevice',
      configuration: 'build/getConfigurationSummary',
      image: 'images/getBuildImage',
      productSummary: 'products/getProductSummary',
      method: 'methods/current',
      plug: 'methods/currentPlug',
    }),
    deliveryTime() {
      if (this.deliveryDays === 1) {
        return this.$t('DeliveryTime.delivered_tomorrow', { days: this.deliveryDays });
      } else {
        return this.$t('DeliveryTime.delivered_in_days', { days: this.deliveryDays });
      }
    },
    selectedCar() {
      return {
        carBrand: this.selectedBrand,
        carModel: this.selectedModel,
      };
    },
    typeTabs() {
      return this.$store.state.types.all.map((t) => {
        return { id: t.id, label: t.name };
      });
    },
    car() {
      if (this.selectedCar && this.selectedCar.carBrand && this.selectedCar.carModel) {
        return this.selectedCar.carBrand.name + ' ' + this.selectedCar.carModel.name;
      } else {
        return '';
      }
    },

    extraWishes: {
      get() {
        return this.$store.getters['build/getWishes'];
      },
      set(value) {
        this.$store.commit('build/SET_WISHES', value);
      },
    },
    infoPopupType() {
      return this.$store.getters['infoPopup/findByKey']('charging_station_type');
    }
  },

  methods: {
    changeType(val, oldVal) {
      this.$store.dispatch('build/changeType', { value: val, old: oldVal });
      this.updateSetting();
    },
  },

  beforeUpdate() {
    this.activeType = this.type.id;
  },
  created() {
    if (this.type) {
      this.activeType = this.type.id;
    }
  },
};
</script>

<style scoped lang="scss">
.configuration {
  @media only screen and (max-width: 840px) {
    margin-top: 35px;
  }
}

.charging-options {
  width: 100%;
  display: flex;
  background: #fff;
  box-shadow: $box-shadow-small;
  border-radius: $border-radius;

  .option {
    min-width: unset;
    width: 100%;
  }

  .block-dropdown {
    border-bottom-left-radius: $border-radius;
    border-top-left-radius: $border-radius;
    border-right: 1px solid $primary-lighter;
  }

  .selected-model {
    min-width: 160px;
    height: 75px;
    display: flex;
    flex-direction: column;
    text-align: center;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    font-weight: 700;
    padding: 5px;
    box-sizing: border-box;
    border-right: 1px solid #e9e9e9;
    cursor: pointer;

    img {
      width: auto;
      height: auto;
      max-width: 75px;
      max-height: 55px;
    }

    span {
      font-weight: 700;
      color: #186cad;
    }
  }
}

.fade-move-left-mobile-enter-active, .fade-move-left-mobile-leave-active {
  transition: all .4s;
}

.fade-move-left-mobile-enter, .fade-move-left-mobile-leave-to /* .fade-leave-active below version 2.1.8 */
{
  opacity: 0;
  transform: translateY(-3rem);
}

.fade-move-left-enter-active, .fade-move-left-leave-active {
  transition: all .4s;
}

.fade-move-left-enter, .fade-move-left-leave-to /* .fade-leave-active below version 2.1.8 */
{
  opacity: 0;
  transform: translateX(-3rem);
}

table {
  margin-top: 10px;
  border-collapse: collapse;
  width: 230px;
  position: absolute;
  left: -20px;

  th {
    font-size: 10px;
    color: rgba(0, 0, 0, .5);

    &:first-child {
      width: 50px;
    }
  }

  td {
    text-align: center;
    font-family: 'AntennaCond', sans-serif;
    font-weight: 700;
    font-size: 10px;
  }

  tr {
    height: 36px;

    &:first-child {
      height: 20px;
    }

    &:nth-child(even):not(:first-child) {
      background-color: #FAFAFA;
    }

    &:nth-child(odd):not(:first-child) {
      background-color: #F4F4F4;
    }
  }
}

.emat-button.open-mobile-menu {
  position: fixed;
  top: 96px;
  left: 0;
  right: 0;
  width: 100%;
  max-width: unset;
  z-index: 1;
  border-top-left-radius: 0;
  border-top-right-radius: 0;

  svg {
    transition: all ease 0.2s;

    &.active {
      transform: rotate(180deg);
    }
  }
}

.mobile-configuration {
  position: fixed;
  top: 127px;
  left: 0;
  bottom: 45px;
  padding-bottom: 10px;
  width: 100%;
  z-index: 1;
  box-shadow: 0 5px 15px rgba(0, 0, 0, .1);
  background-color: #f9f9f9;

  .image-container {
    position: relative;
    height: 100%;
    max-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: calc(100% - 50px);

    img {
      width: 275px;
      min-width: 80%;
      height: inherit;
      max-height: 80vh;
      object-fit: contain;
      object-position: center;
    }
  }

}

.mobile-summary {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  background: #fff;
  transform: translateY(100%) translateY(-47px);
  transition: all .3s ease;
  z-index: 8;

  &.open {
    transform: translateY(0);

    .chevron {
      transform: scaleY(-1);
    }
  }

  .overview-toggle {
    display: flex;
    padding: 12px;
    font-family: $font;
    font-size: 14px;
    line-height: 14px;
    color: #4d4f5c;
    background: #fff;
    box-shadow: 0 5px 15px rgba(0, 0, 0, .1);
    position: relative;
    z-index: 2;

    .overview-label {
      flex-grow: 1;
      padding-top: 6px;

      .chevron {
        width: 12px;
        max-height: 8px;
        display: block;
        float: left;
        margin-right: 5px;
        margin-top: 2px;
        transition: all .1s ease .2s;
      }
    }
  }

  .delivery {
    display: flex;
    align-items: center;
    font-size: 12px;

    .icon {
      margin-right: 6px;
    }

    .strong {
      font-family: $font-black;
    }
  }

  .mobile-overview {
    background: $highlight;
    transition: all .3s ease;
    padding: 16px 20px 0;

    .order-details {
      padding: 20px;
      max-height: 60vh;
      overflow-y: auto;
    }
  }
}
</style>
